import {LoadingOutlined} from "@ant-design/icons";
import Collapse from "@kunukn/react-collapse";
import {Breadcrumb, Button, Checkbox, Descriptions, Form, Input, message, Modal, Select, Spin, Switch} from "antd";
import {useForm} from "antd/es/form/Form";
import React, {useContext, useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {AppContextContext, SbomProjectReportDefinitionServiceContext, SbomProjectServiceContext} from "../../Contexts";
import QueryOptions from "../../sal-ui/QueryOptions";
import {ServerConstraintViolationsHolder} from "../../sal-ui/ServerConstraintViolations";
import {DocumentTitle} from "../DocumentTitle";
import * as globalStyles from "../App.module.css";
import * as styles from "./SbomProjectReportDefinitionDetail.module.css";
import * as tackStyles from "../vulsourceitem/Tack.module.css";
import _ from "lodash";
import {capitalize, formatDateTime} from "../../utils/FormatUtils";
import * as descriptionStyles from "../SelectItemDescription.module.css";
import {Permission} from "../../domain/auth/Permission";
import SbomProjectReportDefinition from "../../domain/SbomProjectReportDefinition";
import SbomProject from "../../domain/SbomProject";
import SelectAllMultiSelect from "../common/SelectAllMultiSelect";
import {definedValues} from "../../utils/SelectOptionUtils";

const serverViolationsHolder = new ServerConstraintViolationsHolder();

function SbomProjectReportDefinitionDetail() {
    const appContext = useContext(AppContextContext);
    const sbomProjectReportDefinitionService = useContext(SbomProjectReportDefinitionServiceContext);
    const sbomProjectService = useContext(SbomProjectServiceContext);
    const navigate = useNavigate();
    const {definitionId}: any = useParams();
    const [editMode, setEditMode] = useState(false);
    const [definition, setDefinition] = useState<SbomProjectReportDefinition>();
    const [editForm] = useForm();
    const [sbomProjects, setSbomProjects] = useState<SbomProject[]>();

    const layout = {
        labelCol: {span: 6},
        wrapperCol: {span: 18},
    };

    const tailLayout = {
        wrapperCol: {offset: 6, span: 18},
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        sbomProjectService.getList(QueryOptions.newUnlimitedOrderedInstance("name")).then(value => {
            setSbomProjects(value.data);
        });

        reload();
    }, [])

    return (
        <DocumentTitle title={definition?.name}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item><Link to={"/sbom-project-report-definitions"}>Report definitions</Link></Breadcrumb.Item>
                    <Breadcrumb.Item>{definition?.name}</Breadcrumb.Item>
                </Breadcrumb>

                <Spin spinning={definition === undefined} indicator={<LoadingOutlined style={{fontSize: 24}} spin={true}/>}>
                    <h1>{definition?.name}</h1>

                    {appContext.user.hasPermission(Permission.REPORT__MANAGE) &&
                        <div className={globalStyles["common__top-button-bar"]}>
                            <Button onClick={() => setEditMode(!editMode)}>Edit</Button>

                            <Button danger={true}
                                    title={"Delete"}
                                    onClick={() => {
                                        Modal.confirm({
                                            content: "Do you really want to delete this report definition?",
                                            okText: "Delete",
                                            cancelText: "Cancel",
                                            okButtonProps: {danger: true},
                                            onOk: () => onDeleteConfirm()
                                        });
                                    }}>Delete</Button>

                            <Button title={"Clone"} onClick={() => navigate(`/sbom-project-report-definitions/clone/${definition.id}`)}>Clone</Button>
                        </div>
                    }

                    {renderEditForm()}

                    <Descriptions column={1} bordered={true} size="small" className={globalStyles.details}>
                        {appContext.user.isSystemAdmin() && <Descriptions.Item label={"Organization"}>
                            <Link to={`/organizations/${definition?.organization?.id}`}>{definition?.organization?.name}</Link>
                        </Descriptions.Item>}
                        <Descriptions.Item label={"Projects"}>
                            {definition?.sbomProjects?.map(sbomProject => (
                                <div><Link key={sbomProject.id} to={`/sbom-projects/${sbomProject.id}`}>{sbomProject.name}</Link></div>
                            ))}
                        </Descriptions.Item>
                        <Descriptions.Item label={"Days"}>{renderDays(definition?.days)}</Descriptions.Item>
                        <Descriptions.Item label={"Hours"}>{definition?.times?.join(", ")}</Descriptions.Item>
                        <Descriptions.Item label={"Time zone"}>{definition?.timeZone}</Descriptions.Item>
                        <Descriptions.Item label={"Enabled"}><Checkbox checked={definition?.enabled} disabled={true}/></Descriptions.Item>
                        <Descriptions.Item label={"Generate empty report"}><Checkbox checked={definition?.generateEmptyReport} disabled={true}/></Descriptions.Item>
                        <Descriptions.Item label={"E-mail"}>{definition?.email}</Descriptions.Item>

                        {appContext.user.isSystemAdmin() && <Descriptions.Item label={"Created at"}>{formatDateTime(definition?.createdAt)}</Descriptions.Item>}
                    </Descriptions>
                </Spin>
            </>
        </DocumentTitle>
    )

    function renderEditForm() {
        return (
            <Collapse isOpen={editMode}>
                <h3>Edit of report definition</h3>

                <Form {...layout} form={editForm} className={`${globalStyles["common__form"]} ${globalStyles["common_form--edit"]}`} onFinish={onFinishEdit}>

                    <Form.Item
                        name={"name"}
                        label={"Name"}
                        rules={[
                            {required: true, message: "Name is required."}
                        ]}>
                        <Input autoFocus={true} maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"sbomProjects"}
                        label={"Projects"}
                        rules={[{required: true, message: "At least one project is required."}]}>
                        <SelectAllMultiSelect
                            selectAllValues={definedValues(sbomProjects, sbomProject => sbomProject.id)}
                            filterOption={(inputValue, option) => {
                                const normalizedInput = inputValue.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
                                const normalizedLabel = option.label?.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");

                                return normalizedLabel.includes(normalizedInput);
                            }}
                            optionLabelProp={"label"}
                            popupMatchSelectWidth={350}
                            virtual={false}
                            listHeight={300}
                        >
                            {sbomProjects?.map(sbomProject =>
                                <Select.Option key={sbomProject.id} value={sbomProject.id} label={sbomProject.name}>
                                    <div className={`${tackStyles.item}`}>
                                        <div className={descriptionStyles['name-container']}>
                                            <span className={descriptionStyles.name}>{sbomProject.name}</span>
                                        </div>
                                    </div>
                                </Select.Option>
                            )}
                        </SelectAllMultiSelect>
                    </Form.Item>

                    <Form.Item
                        name={"days"}
                        label={"Days"}>
                        <Checkbox.Group className={styles.days}>
                            <Checkbox value={"MONDAY"}>Monday</Checkbox>
                            <Checkbox value={"TUESDAY"}>Tuesday</Checkbox>
                            <Checkbox value={"WEDNESDAY"}>Wednesday</Checkbox>
                            <Checkbox value={"THURSDAY"}>Thursday</Checkbox>
                            <Checkbox value={"FRIDAY"}>Friday</Checkbox>
                            <Checkbox value={"SATURDAY"}>Saturday</Checkbox>
                            <Checkbox value={"SUNDAY"}>Sunday</Checkbox>
                        </Checkbox.Group>
                    </Form.Item>

                    <Form.Item
                        name={"times"}
                        label={"Hours"}>
                        <Checkbox.Group
                            className={styles.times}
                            options={_.range(24).map(index => String(index).padStart(2, '0') + ":00")}
                        />
                    </Form.Item>

                    <Form.Item
                        name={"timeZone"}
                        label={"Time zone"}
                        initialValue={new Intl.DateTimeFormat().resolvedOptions().timeZone}
                        rules={[{required: true, message: "Time zone is required."}]}>
                        <Select
                            options={Intl.supportedValuesOf('timeZone').map(value => {
                                return {label: value, value}
                            })}
                            showSearch={true}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name={"email"}
                        label={"E-mail"}
                        extra={"Comma separated list of email addresses."}
                        rules={[{required: true, message: "At least one e-mail address is required."}]}>
                        <Input maxLength={100}/>
                    </Form.Item>

                    <Form.Item
                        name={"enabled"}
                        label={"Enabled"}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    <Form.Item
                        name={"generateEmptyReport"}
                        label={"Generate empty report"}
                        valuePropName={"checked"}>
                        <Switch/>
                    </Form.Item>

                    <Form.Item {...tailLayout} className={globalStyles["common__form-buttons"]}>
                        <Button type={"primary"} htmlType={"submit"}>{"Save"}</Button>
                        <Button onClick={() => setEditMode(false)}>{"Cancel"}</Button>
                    </Form.Item>
                </Form>
            </Collapse>
        )
    }

    function renderDays(days?: string[]) {
        if (days?.length == 7) {
            return <i>every day</i>;
        }

        return days?.map(d => capitalize(d.toLowerCase())).join(", ");
    }

    function onFinishEdit(values: any) {
        sbomProjectReportDefinitionService.update(definitionId, values)
            .then(
                (id) => {
                    message.success(<>Report definition updated.</>);

                    setEditMode(false);

                    reload();
                },
                error => serverViolationsHolder.handleServerError(error, editForm)
            );
    }

    function onDeleteConfirm() {
        sbomProjectReportDefinitionService.delete(definition)
            .then(() => {
                message.success(<>Report definition <b>{definition.name}</b> deleted.</>);

                navigate(`/sbom-project-report-definitions`);
            });
    }

    function reload() {
        sbomProjectReportDefinitionService.get(definitionId).then(definition => {
            setDefinition(definition);

            editForm.setFieldsValue({
                ...definition,
                sbomProjects: definition.sbomProjects.map(value => value.id)
            });
        });
    }

}

export default SbomProjectReportDefinitionDetail;
